import type { FieldDef, FormButtonElement, FormCockpitElement, FormElement, FormRenderPlan } from "../core";
import { FormCockpit } from "./FormCockpit";
import { FormHeader } from "./FormHeader";
import { FormSheet } from "./FormSheet";
import { FormSummary } from "./FormSummary";
import { RecordTimeline } from "./RecordTimeline";
import type { TimelineFilter } from "./RecordStrip";
import { useFormStateContext } from "../react/form/RecordProvider";

interface FormViewProps {
    title: string;
    recordRef: string;
    modelKey: string;
    actionSlug: string;
    fieldDefs: FieldDef[];
    isLoading: boolean;
    formPlan?: FormRenderPlan | null;
    /** Strip chip jump signal → timeline filter + scroll (Enh 03 S4). */
    timelineJump?: { filter: TimelineFilter; n: number } | null;
    /** Bumped on chatter mutations → timeline refetch. */
    timelineRefresh?: number;
    /** Bubbled when the timeline composer posts (strip counts refresh). */
    onChatterMutated?: () => void;
    onRunButton?: (button: FormButtonElement) => void;
    onRunTransition?: (code: string, name: string) => Promise<void> | void;
}

// FormView — header strip + sheet body. Read-mode rendering for WS 4.2;
// edit widgets and chatter aside land in WS 4.3 / 4.6.
//
// `RecordProvider` is wrapped around this component by ActionManager, so
// `<FormSheet>` reads draft/isEditing/etc. via `useFormStateContext`. The
// draft is also passed to FormHeader as `record` for per-button visibility
// evaluation against the live edit buffer.
export function FormView({
    title,
    recordRef,
    modelKey,
    actionSlug,
    fieldDefs,
    isLoading,
    formPlan,
    timelineJump,
    timelineRefresh,
    onChatterMutated,
    onRunButton,
    onRunTransition,
}: FormViewProps) {
    const { draft, isNew: isNewDraft } = useFormStateContext();
    const isNew = recordRef === "new" || isNewDraft;
    const sheetElements = formPlan?.sheet ?? [];
    const headerRecord = isNew || !draft || Object.keys(draft).length <= 1 ? null : draft;
    // Form-view revamp (Enh 14) is the DEFAULT form chrome — NOT gated. Every
    // form renders the flat-modern body (section dividers + inline label:value
    // cells) inside an elevated form-main card. <summary> stays optional: it
    // only adds the title/KPI header when the view declares one.
    const isRevamp = Boolean(formPlan);

    // Action buttons declared in <header> surface in the cockpit Status card
    // (Enh 14) — the user-facing verbs (transitions/commands) live with the
    // status. Save/Cancel/Edit chrome (special) stays in the toolbar.
    const headerButtons = (formPlan?.header ?? []).filter(
        (el): el is FormButtonElement => el.type === "button" && !el.special,
    );

    // Cockpit auto-derivation (Enh 14): the in-header statusbar + action verbs
    // are DEPRECATED — a declared <header> with anything actionable (a statusbar
    // OR action buttons) auto-generates the cockpit Status card, with no
    // per-view <cockpit> opt-in. Everything moves into that card: the stepper +
    // transitions + header verbs; the top header strip is left empty (and so
    // self-suppresses). The status field comes from whichever statusbar form the
    // header carries (mirrors FormHeader.isStatusbarEl):
    //   <statusbar field="..."/>               → workflow-driven → el.field
    //   <field name="..." widget="statusbar"/> → enum-driven     → el.name
    // A header with ONLY buttons (no status) still produces the card (field
    // null → an "Actions" card carrying the verbs). An explicit <cockpit> wins.
    const headerStatusEl = (formPlan?.header ?? []).find(
        (e: FormElement) => e.type === "statusbar" || (e.type === "field" && e.widget === "statusbar"),
    );
    const headerStatusField = headerStatusEl
        ? headerStatusEl.type === "statusbar"
            ? headerStatusEl.field
            : headerStatusEl.type === "field"
              ? headerStatusEl.name
              : null
        : null;
    // Mirror the enum statusbar's visible/ordered subset (statusbar_visible) so
    // the cockpit rail matches the header bar it replaces. Workflow statusbars
    // drive their rail from the graph, so this only applies to the enum form.
    const headerStatusVisible =
        headerStatusEl?.type === "field"
            ? (headerStatusEl.options?.statusbar_visible ?? "")
                  .split(",")
                  .map((s) => s.trim())
                  .filter(Boolean)
            : [];
    const hasActionableHeader = headerStatusField != null || headerButtons.length > 0;
    const explicitCockpit =
        formPlan?.cockpit && formPlan.cockpit.components.length > 0 ? formPlan.cockpit : null;
    const effectiveCockpit: FormCockpitElement | null =
        explicitCockpit ??
        (hasActionableHeader
            ? {
                  auto: true,
                  width: null,
                  components: [
                      {
                          type: "status",
                          field: headerStatusField,
                          showAge: true,
                          buttons: [],
                          statusbarVisible: headerStatusVisible.length ? headerStatusVisible : null,
                      },
                  ],
              }
            : null);

    // Cockpit pane (Enh 14 · Stage 2): a 2-column body (form-main + form-cockpit)
    // when a cockpit (declared OR auto-derived from the header) has a component.
    const hasCockpit = Boolean(effectiveCockpit && effectiveCockpit.components.length > 0);

    // When the cockpit carries a <status> card, it owns the statusbar +
    // transitions + header action verbs — so the header strip suppresses them
    // to avoid a duplicate top row (revamp dedup).
    const cockpitHasStatus = Boolean(
        effectiveCockpit?.components.some((c) => c.type === "status"),
    );

    // Whether the top header strip will actually render visible content. With
    // the statusbar + verbs deprecated into the cockpit, a header that carried
    // ONLY those is now empty — treat it as no-header so the body keeps its top
    // padding (the `--no-header` modifier) instead of sitting flush under an
    // invisible strip. `<related>` blocks render in the toolbar, never here.
    const headerVisibleEls = (formPlan?.header ?? []).filter((el) => {
        if (el.type === "related") return false;
        if (
            cockpitHasStatus &&
            (el.type === "button" || el.type === "statusbar" || (el.type === "field" && el.widget === "statusbar"))
        ) {
            return false;
        }
        return true;
    });
    const hasHeader = headerVisibleEls.length > 0;

    return (
        <div
            className={`form-view${hasHeader ? "" : " form-view--no-header"}${isRevamp ? " form-view--revamp" : ""}`}
            data-viewid="ede-form"
        >
            {hasHeader && formPlan ? (
                <FormHeader
                    elements={formPlan.header}
                    record={headerRecord}
                    fieldDefs={fieldDefs}
                    actionSlug={actionSlug}
                    modelKey={modelKey}
                    recordRef={isNew ? null : recordRef}
                    onRunButton={onRunButton}
                    onRunTransition={onRunTransition}
                    suppressWorkflow={cockpitHasStatus}
                />
            ) : null}
            <div className={`form-body${hasCockpit ? " form-body--cockpit" : ""}`}>
                <div className="form-primary">
                <div className="form-main">
                    {formPlan?.summary ? <FormSummary summary={formPlan.summary} record={isNew ? null : draft} fieldDefs={fieldDefs} /> : null}
                    {sheetElements.length > 0 ? (
                        <FormSheet
                            elements={sheetElements}
                            fieldDefs={fieldDefs}
                            actionSlug={actionSlug}
                            modelKey={modelKey}
                            recordRef={isNew ? null : recordRef}
                            onRunButton={onRunButton}
                        />
                    ) : (
                        <div className="form-sheet">
                            <div className="form-sheet__title">
                                <span className="form-sheet__title-input">{isNew ? "New record" : title}</span>
                            </div>
                            {isLoading ? (
                                <p className="workspace-placeholder__note">Loading…</p>
                            ) : (
                                <p className="workspace-placeholder__note">No sheet declared in the view DSL.</p>
                            )}
                        </div>
                    )}
                </div>
                    {/* Discuss panel — INSIDE the main column so it aligns to the
                        form width with the cockpit alongside (mockup `m-discuss`),
                        rather than spanning full-width below both columns. Renders
                        only when a timeline-bearing component survived gating and
                        the record is saved. */}
                    <RecordTimeline
                        components={formPlan?.activity ?? []}
                        modelKey={modelKey}
                        recordUuid={isNew ? undefined : recordRef}
                        fieldDefs={fieldDefs}
                        jumpSignal={timelineJump}
                        refreshSignal={timelineRefresh}
                        onMutated={onChatterMutated}
                    />
                </div>
                {hasCockpit && effectiveCockpit ? (
                    <FormCockpit
                        cockpit={effectiveCockpit}
                        record={isNew ? null : draft}
                        modelKey={modelKey}
                        recordRef={isNew ? null : recordRef}
                        fieldDefs={fieldDefs}
                        actionSlug={actionSlug}
                        headerButtons={headerButtons}
                        onRunButton={onRunButton}
                        onRunTransition={onRunTransition}
                    />
                ) : null}
            </div>
        </div>
    );
}
